import { requestKarangoApi } from "../../core/api/karangoTransport";
import { auth } from "../../core/firebase/firebase";
import { captureAuthSession } from "../../core/auth/authSession";

// ── Mémoire courte de la liste des trajets d'une sortie ─────────────────────
// La fiche de sortie charge cette liste (pastille de l'onglet Covoiturage) ;
// la charte d'inscription, ouverte juste après, peut la réutiliser au lieu de
// la redemander. Garde-fous :
// - clé par compte connecté : la liste dépend du lecteur (statut de sa
//   demande, droit de demander une place) et n'est jamais servie à un autre ;
// - lecture en mémoire uniquement si l'appelant la demande (maxAgeMs > 0) :
//   le compteur et l'entree dans l'onglet acceptent 15 s au maximum ;
// - vidée par toute action sur les trajets de la sortie ;
// - une requête en cours est partagée plutôt que doublée ;
// - les erreurs ne sont jamais mémorisées ;
// - conservation bornée : une liste (qui peut contenir des numéros de
//   téléphone par trajet) est retirée de la mémoire au plus tard
//   OFFERS_CACHE_RETENTION_MS après sa réception, même si l'écran est fermé.
export const OFFERS_CACHE_RETENTION_MS = 15000;
const offersCache = new Map();

function storeOffers(key, result, session) {
  const entry = { result, storedAt: Date.now(), pending: null, session };
  offersCache.set(key, entry);
  setTimeout(() => {
    if (offersCache.get(key) === entry) offersCache.delete(key);
  }, OFFERS_CACHE_RETENTION_MS);
}

function offersCacheKey(eventId) {
  const viewerId = auth?.currentUser?.uid || "anonymous";
  return `${viewerId}:${String(eventId)}`;
}

export function invalidateCarpoolOffersCache(eventId) {
  if (eventId == null) {
    offersCache.clear();
    return;
  }
  const suffix = `:${String(eventId)}`;
  for (const key of offersCache.keys()) {
    if (key.endsWith(suffix)) offersCache.delete(key);
  }
}

async function fetchCarpoolOffers(eventId, signal) {
  const payload = await requestKarangoApi(
    `/v1/events/${encodeURIComponent(String(eventId))}/carpool/offers`,
    { requiresAuth: true, signal }
  );
  return {
    offers: Array.isArray(payload?.offers) ? payload.offers : [],
    viewerCanAct: payload?.viewerCanAct !== false,
    viewerParticipationStatus: typeof payload?.viewerParticipationStatus === "string" ? payload.viewerParticipationStatus : null,
  };
}

// Onglet Covoiturage : trajets et droit d'agir du lecteur (proposer sa
// voiture, demander une place), relus apres chaque action. Un serveur plus
// ancien sans ce champ laisse agir (le serveur refuse de toute façon).
// Visiteur non connecte : apercu anonyme des trajets (ville, heure, places,
// prix ; aucune identite ni point de depart precis). Jamais mis en memoire.
export async function listPublicCarpoolOffers(eventId, { signal } = {}) {
  const payload = await requestKarangoApi(
    `/v1/events/${encodeURIComponent(String(eventId))}/carpool/public-offers`,
    { requiresAuth: false, signal }
  );
  return Array.isArray(payload?.offers) ? payload.offers : [];
}

function abortOffersRead() {
  return Object.assign(new Error("Lecture des trajets annulee."), { name: "AbortError" });
}

// Chaque lecteur annule son attente, sans annuler le transport partage.
function waitForOffers(pending, signal) {
  if (!signal) return pending;
  if (signal.aborted) return Promise.reject(abortOffersRead());
  return new Promise((resolve, reject) => {
    const onAbort = () => reject(abortOffersRead());
    signal.addEventListener("abort", onAbort, { once: true });
    pending.then((result) => {
      signal.removeEventListener("abort", onAbort);
      resolve(result);
    }, (error) => {
      signal.removeEventListener("abort", onAbort);
      reject(error);
    });
  });
}

export async function listCarpoolOffersForViewer(eventId, { signal, maxAgeMs = 0 } = {}) {
  if (signal?.aborted) throw abortOffersRead();
  const key = offersCacheKey(eventId);
  const cached = offersCache.get(key);

  const maxAge = Math.min(Number(maxAgeMs) || 0, OFFERS_CACHE_RETENTION_MS);

  if (maxAge > 0 && cached?.session?.isCurrent()) {
    if (cached.result && Date.now() - cached.storedAt <= maxAge) {
      return waitForOffers(Promise.resolve(cached.result), signal);
    }
    if (cached.pending) {
      return waitForOffers(cached.pending, signal);
    }
  }

  const session = captureAuthSession();
  const pending = fetchCarpoolOffers(eventId).then((result) => {
    if (offersCache.get(key)?.pending === pending) {
      if (session.isCurrent()) storeOffers(key, result, session);
      else offersCache.delete(key);
    }
    return result;
  }, (error) => {
    if (offersCache.get(key)?.pending === pending) {
      offersCache.delete(key);
    }
    throw error;
  });
  offersCache.set(key, { pending, session });
  return waitForOffers(pending, signal);
}

export async function listCarpoolOffers(eventId, options = {}) {
  return (await listCarpoolOffersForViewer(eventId, options)).offers;
}

// Toute action sur les trajets vide la mémoire de la sortie, même en cas
// d'échec (l'état serveur a pu changer).
async function withOffersInvalidation(eventId, run) {
  try {
    return await run();
  } finally {
    invalidateCarpoolOffersCache(eventId);
  }
}

export async function createCarpoolOffer(eventId, values) {
  return withOffersInvalidation(eventId, async () => {
    const payload = await requestKarangoApi(
      `/v1/events/${encodeURIComponent(String(eventId))}/carpool/offers`,
      { method: "POST", requiresAuth: true, body: values }
    );
    return payload?.offer || null;
  });
}

export async function updateCarpoolOffer(eventId, offerId, values) {
  return withOffersInvalidation(eventId, async () => {
    const payload = await requestKarangoApi(
      `/v1/events/${encodeURIComponent(String(eventId))}/carpool/offers/${encodeURIComponent(String(offerId))}`,
      { method: "PATCH", requiresAuth: true, body: values }
    );
    return payload?.offer || null;
  });
}

export async function joinCarpoolOffer(eventId, offerId, values = {}) {
  return withOffersInvalidation(eventId, async () => {
    const payload = await requestKarangoApi(
      `/v1/events/${encodeURIComponent(String(eventId))}/carpool/offers/${encodeURIComponent(String(offerId))}/join`,
      { method: "POST", requiresAuth: true, body: values }
    );
    return payload?.offer || null;
  });
}

// Corps d'une reponse du conducteur : son numero de telephone seulement pour
// accepter (docs/carpool-trip-phone-spec.md), jamais envoye pour un refus.
export function buildCarpoolReviewValues(decision, responsePhone = "") {
  return decision === "accept" && responsePhone ? { responsePhone } : {};
}

export async function reviewCarpoolRequest(eventId, offerId, requestId, decision, values = {}) {
  return withOffersInvalidation(eventId, async () => {
    const payload = await requestKarangoApi(
      `/v1/events/${encodeURIComponent(String(eventId))}/carpool/offers/${encodeURIComponent(String(offerId))}/requests/${encodeURIComponent(String(requestId))}/${decision}`,
      { method: "POST", requiresAuth: true, body: values }
    );
    return payload?.offer || null;
  });
}

export async function cancelCarpoolMembership(eventId, offerId, values = {}) {
  return withOffersInvalidation(eventId, async () => {
    const payload = await requestKarangoApi(
      `/v1/events/${encodeURIComponent(String(eventId))}/carpool/offers/${encodeURIComponent(String(offerId))}/cancel`,
      { method: "POST", requiresAuth: true, body: values }
    );
    return payload?.offer || null;
  });
}

// Vue transverse "Mes covoiturages" : memes offres/demandes que l'onglet
// Covoiturage d'un evenement, agregees cote serveur pour l'utilisateur.
export async function getMyCarpools({ signal } = {}) {
  return requestKarangoApi("/v1/me/carpool", { requiresAuth: true, signal });
}

// Recherche transverse (independante de l'evenement auquel participe le
// viewer) dans la zone VISIBLE de la carte : c'est le perimetre unique de la
// vue Rechercher (cf. docs/carpool-nearby-search-spec.md). `reference` (position
// de l'utilisateur, facultative) ne sert qu'au tri et a l'affichage de la
// distance, jamais a filtrer.
export async function searchCarpoolOffersInBounds({ bounds, reference = null, filters = {}, signal } = {}) {
  const params = new URLSearchParams();
  params.set("north", String(bounds.north));
  params.set("south", String(bounds.south));
  params.set("east", String(bounds.east));
  params.set("west", String(bounds.west));
  for (const key of ["departureFrom", "departureBefore", "maxPriceCents", "minSeats", "sort"]) {
    if (filters[key] != null && filters[key] !== "") params.set(key, String(filters[key]));
  }
  if (reference && Number.isFinite(reference.lat) && Number.isFinite(reference.lng)) {
    params.set("refLat", String(reference.lat));
    params.set("refLng", String(reference.lng));
  }
  const payload = await requestKarangoApi(`/v1/carpool/search?${params}`, {
    requiresAuth: true,
    signal,
  });
  return Array.isArray(payload?.offers) ? payload.offers : [];
}

export async function cancelCarpoolOffer(eventId, offerId, values = {}) {
  return withOffersInvalidation(eventId, async () => {
    const payload = await requestKarangoApi(
      `/v1/events/${encodeURIComponent(String(eventId))}/carpool/offers/${encodeURIComponent(String(offerId))}`,
      { method: "DELETE", requiresAuth: true, body: values }
    );
    return payload?.offer || null;
  });
}

// ── Demande dans la messagerie (docs/carpool-request-messaging-spec.md) ────

// Etat vivant des cartes « Demande de place » d'une conversation (1 a 30).
export async function getCarpoolRequestCards(items, { signal } = {}) {
  const payload = await requestKarangoApi("/v1/carpool/request-cards", {
    method: "POST",
    requiresAuth: true,
    body: { items },
    signal,
  });
  return payload?.cards || {};
}

// Ouvre la conversation d'une demande (conducteur ou passager) ; threadId
// null s'il n'existe encore aucune conversation.
export async function openCarpoolRequestConversation(membershipId) {
  const payload = await requestKarangoApi(
    `/v1/carpool/requests/${encodeURIComponent(String(membershipId))}/conversation`,
    { method: "POST", requiresAuth: true, body: {} }
  );
  return { threadId: payload?.threadId || null, otherUserId: payload?.otherUserId || null };
}
